/* eslint-disable @angular-eslint/directive-class-suffix */
import { Directive } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { fromEvent } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

/**
 * Simple utility class to aggregate common logic used on forms.
 *
 * @export
 * @class FormHelper
 */
@Directive()
export class FormHelper {
  /**
   * Recursive method that iterates through every control within the provided formGroup/formArray
   * and triggers its associated validation.
   * As an optional behaviour, the function can receive a host component.
   * With host component, it requires change detector as the UI won't have the invalid state
   * without the angular change detection kicks in. Also, it's using the change detector
   * to avoid usage of SetTimeout, which can be unreliable.
   *
   */
  public static validateAllFormFields(
    formGroup: FormGroup | FormArray,
    onlySelf = false
  ): void {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control) {
        if (control instanceof FormGroup || control instanceof FormArray) {
          this.validateAllFormFields(control, onlySelf);
        }
        this.triggerValidation(control, onlySelf);
      }
    });
  }

  /** Adds behaviour that finds the first invalid field on the screen and scrolls to it */
  public static scrollToFirstInvalidControl(host: HTMLElement): void {
    const element = host.querySelector('.ng-invalid') as HTMLElement;
    if (element && window?.scroll) {
      window.scroll({
        top: this.getTopOffset(element),
        left: 0,
        behavior: 'smooth',
      });

      fromEvent(window, 'scroll')
        .pipe(debounceTime(100), take(1))
        .subscribe(() => element.focus());
    }
  }

  private static getTopOffset(control: HTMLElement): number {
    const labelOffset = 50;
    return control.getBoundingClientRect().top + window.scrollY - labelOffset;
  }

  private static triggerValidation(
    control: AbstractControl,
    onlySelf = true
  ): void {
    control.markAsDirty({ onlySelf });
    control.markAsTouched({ onlySelf });
    control.updateValueAndValidity({ onlySelf });
  }
}
